import { useState } from "react";
import { toast } from "sonner";
import { Shield, Phone, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/i18n/use-translation";
import { authErrorMessage } from "@/lib/auth-error";

export default function SettingsSecurityPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [unenrolling, setUnenrolling] = useState(false);

  const phoneFactor = user?.factors?.find(
    (f) => f.factor_type === "phone" && f.status === "verified",
  );

  async function handleEnroll() {
    if (!phone.trim()) return;
    setEnrolling(true);

    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "phone",
      phone: phone.trim(),
    });

    if (error) {
      toast.error(authErrorMessage(t, error));
      setEnrolling(false);
      return;
    }

    setFactorId(data.id);

    // Send the first challenge
    const { data: challenge, error: chalError } = await supabase.auth.mfa.challenge({
      factorId: data.id,
      channel: "sms",
    });

    if (chalError) {
      toast.error(chalError.message);
      setEnrolling(false);
      return;
    }

    setChallengeId(challenge.id);
    setEnrolling(false);
    toast.success(t("settings.codeSent"));
  }

  async function handleVerify() {
    if (!factorId || !challengeId) return;
    setVerifying(true);

    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code: otp.trim(),
    });

    if (error) {
      toast.error(authErrorMessage(t, error));
      setVerifying(false);
      return;
    }

    toast.success(t("settings.twoFactorEnabled"));
    setFactorId(null);
    setChallengeId(null);
    setPhone("");
    setOtp("");
    setVerifying(false);

    // Refresh session to get updated factors
    await supabase.auth.refreshSession();
  }

  async function handleUnenroll() {
    if (!phoneFactor) return;
    setUnenrolling(true);

    const { error } = await supabase.auth.mfa.unenroll({
      factorId: phoneFactor.id,
    });

    if (error) {
      toast.error(authErrorMessage(t, error));
      setUnenrolling(false);
      return;
    }

    toast.success(t("settings.twoFactorDisabled"));
    setUnenrolling(false);
    await supabase.auth.refreshSession();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-medium">{t("settings.securityTitle")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("settings.securityDescription")}</p>
      </div>

      <div>
        <h2 className="text-sm font-semibold">{t("settings.twoFactor")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("settings.twoFactorDescription")}</p>
      </div>

      {phoneFactor ? (
        <div className="flex items-center justify-between rounded-md border p-4">
          <div className="flex items-center gap-3">
            <Shield className="size-5 text-emerald-600" />
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{t("settings.phoneVerification")}</p>
                <Badge className="border-transparent bg-emerald-50 text-emerald-700">
                  {t("settings.enabled")}
                </Badge>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                <Phone className="mr-1 inline size-3" />
                {((phoneFactor as Record<string, unknown>).phone as string) ??
                  t("settings.phoneOnFile")}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 hover:text-red-700"
            onClick={handleUnenroll}
            disabled={unenrolling}
          >
            <Trash2 className="mr-1 size-3.5" />
            {unenrolling ? t("settings.removing") : t("settings.remove")}
          </Button>
        </div>
      ) : challengeId ? (
        <div className="max-w-sm space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("settings.enterCodeSentTo", { phone })}
          </p>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="enroll-otp" className="text-sm font-medium">
              {t("settings.verificationCode")}
            </label>
            <Input
              id="enroll-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
          </div>
          <Button onClick={handleVerify} disabled={verifying || otp.length < 6}>
            {verifying ? t("settings.verifying") : t("settings.verifyAndEnable")}
          </Button>
        </div>
      ) : (
        <div className="max-w-sm space-y-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="enroll-phone" className="text-sm font-medium">
              {t("settings.phoneNumber")}
            </label>
            <Input
              id="enroll-phone"
              type="tel"
              placeholder={t("settings.phonePlaceholder")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t("settings.includeCountryCode")}</p>
          </div>
          <Button onClick={handleEnroll} disabled={enrolling || !phone.trim()}>
            {enrolling ? t("settings.sendingCode") : t("settings.enableTwoFactor")}
          </Button>
        </div>
      )}
    </div>
  );
}
